import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

export const getDefaultBackendUrl = () => {
  if (typeof window !== "undefined" && !LOCAL_HOSTS.has(window.location.hostname)) {
    return window.location.origin;
  }
  return "http://127.0.0.1:8001";
};

const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "");
const configuredBackendIsLocal = (() => {
  if (!configuredBackendUrl) return false;
  try {
    return LOCAL_HOSTS.has(new URL(configuredBackendUrl).hostname);
  } catch {
    return false;
  }
})();
const hostedPage = typeof window !== "undefined" && !LOCAL_HOSTS.has(window.location.hostname);
const BACKEND_URL = hostedPage && (!configuredBackendUrl || configuredBackendIsLocal)
  ? window.location.origin
  : configuredBackendUrl || getDefaultBackendUrl();
const BACKEND_UNAVAILABLE_MESSAGE = BACKEND_URL === "http://127.0.0.1:8001"
  ? "Backend unavailable — start the API at http://127.0.0.1:8001"
  : "Backend unavailable — check the Control Room API";

export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 15000,
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401 && typeof window !== "undefined") {
      window.dispatchEvent(new Event("atlas:auth-expired"));
    }
    return Promise.reject(error);
  },
);

export const getErrorMessage = (error) =>
  (typeof error === "string" ? error : null) ||
  (error?.code === "ERR_NETWORK" || error?.message === "Network Error"
    ? BACKEND_UNAVAILABLE_MESSAGE
    : null) ||
  (error?.response?.status === 401 ? "Authentication required — sign in again" : null) ||
  (error?.response?.status === 429 ? "Rate limit reached — retrying shortly" : null) ||
  (error?.response?.status === 503 ? "Service temporarily unavailable — check system health" : null) ||
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.message ||
  "Request failed";

export const get = (path, config) => api.get(path, config);
export const post = (path, data, config) => api.post(path, data, config);
export const put = (path, data, config) => api.put(path, data, config);
export const apiDelete = (path, config) => api.delete(path, config);

const inFlightPollGets = new Map();

const pollGet = (path) => {
  const existing = inFlightPollGets.get(path);
  if (existing) return existing;
  const request = get(path).finally(() => {
    if (inFlightPollGets.get(path) === request) inFlightPollGets.delete(path);
  });
  inFlightPollGets.set(path, request);
  return request;
};

export const pollRetryDelay = (interval, attempt) => {
  const ceiling = Math.max(Number(interval) || 0, 30000);
  const exponent = Math.max(0, Math.min((Number(attempt) || 1) - 1, 5));
  return Math.min(ceiling, 5000 * 2 ** exponent);
};

function snapshotAccountId(snapshot) {
  const accountId = snapshot?.meta?.account_id || snapshot?.status?.exchange?.account_id;
  return typeof accountId === "string" && accountId ? accountId : null;
}

export function useControlRoomStream() {
  const [state, setState] = useState({
    connected: false,
    error: null,
    lastEventAt: null,
    lastEventId: null,
    snapshots: {},
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.EventSource === "undefined") {
      setState((previous) => ({
        ...previous,
        error: "Live control-room stream is unavailable in this browser",
      }));
      return undefined;
    }

    let active = true;
    const stream = new window.EventSource(`${API}/events/stream`, { withCredentials: true });
    const update = (updater) => {
      if (active) setState(updater);
    };
    const handleSnapshot = (event) => {
      try {
        const snapshot = JSON.parse(event.data);
        const accountId = snapshotAccountId(snapshot);
        if (!accountId) return;
        update((previous) => {
          // Heartbeat-style repeats are common; only rebuild the snapshots map
          // when this account's payload actually changed.
          const unchanged = payloadEqual(previous.snapshots[accountId], snapshot);
          return {
            ...previous,
            connected: true,
            error: null,
            lastEventAt: Date.now(),
            lastEventId: event.lastEventId || previous.lastEventId,
            snapshots: unchanged
              ? previous.snapshots
              : { ...previous.snapshots, [accountId]: snapshot },
          };
        });
      } catch {
        update((previous) => ({
          ...previous,
          error: "Received an invalid control-room snapshot",
        }));
      }
    };

    stream.onopen = () => {
      update((previous) =>
        previous.connected && !previous.error
          ? previous
          : { ...previous, connected: true, error: null });
    };
    stream.onerror = () => {
      update((previous) =>
        !previous.connected && previous.error
          ? previous
          : {
              ...previous,
              connected: false,
              error: "Live control-room stream is reconnecting",
            });
    };
    stream.addEventListener("control_room.snapshot", handleSnapshot);

    return () => {
      active = false;
      stream.removeEventListener("control_room.snapshot", handleSnapshot);
      stream.close();
    };
  }, []);

  return state;
}

export const buildPagePath = (path, limit, offset) => {
  const [pathname, queryString] = path.split("?", 2);
  const params = new URLSearchParams(queryString || "");
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  return `${pathname}?${params.toString()}`;
};

const dedupeRowsById = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = row?.id;
    if (key == null || seen.has(key)) return key == null;
    seen.add(key);
    return true;
  });
};

// Same idea as `rowsEqual`, for the single-object payloads `usePoll` returns.
const payloadEqual = (a, b) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== "object" || typeof b !== "object") return false;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

// Polls return identical payloads most of the time. Comparing before storing
// keeps the state reference stable so memoized consumers skip the re-render.
const rowsEqual = (a, b) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const left = a[i];
    const right = b[i];
    if (left === right) continue;
    if (left?.id != null && right?.id != null) {
      if (left.id !== right.id) return false;
      if (JSON.stringify(left) !== JSON.stringify(right)) return false;
      continue;
    }
    if (JSON.stringify(left) !== JSON.stringify(right)) return false;
  }
  return true;
};

// Overlay a freshly fetched first page onto the pages already loaded via
// `loadMore`. Rows the refresh replaced are dropped; everything the user
// scrolled to is kept, so pagination survives a background refresh.
const mergeRefreshedPage = (rows, retained, pageSize) => {
  if (!Array.isArray(retained) || retained.length <= pageSize) return rows;
  const refreshedIds = new Set(
    rows.map((row) => row?.id).filter((id) => id != null),
  );
  const tail = retained.filter((row) => {
    const id = row?.id;
    return id != null && !refreshedIds.has(id);
  });
  return dedupeRowsById([...rows, ...tail]);
};

export function usePoll(path, interval = 4000, initial = null, { enabled = true } = {}) {
  const [state, setState] = useState({
    path,
    data: initial,
    error: null,
    lastSuccessAt: null,
  });
  const [retryKey, setRetryKey] = useState(0);
  const timer = useRef(null);
  const attempt = useRef(0);
  const initialData = useRef(initial);

  useEffect(() => {
    if (!enabled || !path) return undefined;
    let active = true;
    attempt.current = 0;
    setState((prev) => prev.path === path ? prev : {
      path,
      data: initialData.current,
      error: null,
      lastSuccessAt: null,
      lastAttemptAt: null,
    });

    const load = async () => {
      let shouldRetry = true;
      try {
        const res = await pollGet(path);
        if (active) {
          attempt.current = 0;
          setState((prev) => ({
            ...prev,
            path,
            // Reuse the previous reference when the payload is unchanged so
            // memoized consumers are not re-rendered by every poll tick.
            data: payloadEqual(prev.data, res.data) ? prev.data : res.data,
            error: null,
            lastSuccessAt: Date.now(),
            lastAttemptAt: Date.now(),
          }));
        }
      } catch (error) {
        if (active) {
          attempt.current += 1;
          const is401 = error?.response?.status === 401;
          setState((prev) => ({
            ...prev,
            error: getErrorMessage(error),
            lastAttemptAt: Date.now(),
          }));
          if (is401) shouldRetry = false;
        }
      } finally {
        if (active && shouldRetry) {
          const retryDelay = pollRetryDelay(interval, attempt.current);
          const jitter = Math.round(Math.random() * Math.min(1000, retryDelay * 0.15));
          timer.current = setTimeout(load, (attempt.current ? retryDelay : interval) + jitter);
        }
      }
    };

    load();

    return () => {
      active = false;
      clearTimeout(timer.current);
    };
  }, [path, interval, retryKey, enabled]);

  // Stable identity: callers put `retry` in useCallback/useMemo dependency
  // arrays, and a fresh arrow on every render would defeat every one of them.
  const retry = useCallback(() => setRetryKey((value) => value + 1), []);

  const scopedState = state.path === path
    ? state
    : { path, data: initialData.current, error: null, lastSuccessAt: null, lastAttemptAt: null };
  return {
    ...scopedState,
    isPaused: !enabled,
    isStale: Boolean(scopedState.error && scopedState.lastSuccessAt),
    isUnavailable: Boolean(scopedState.error && !scopedState.lastSuccessAt),
    retry,
  };
}

export function useInfinitePoll(
  path,
  interval = 4000,
  initial = [],
  pageSize = 10,
  { prependOnLoadMore = false, enabled = true } = {},
) {
  const [state, setState] = useState({
    path,
    data: initial,
    error: null,
    lastSuccessAt: null,
    hasMore: true,
    loadingMore: false,
  });
  const [retryKey, setRetryKey] = useState(0);
  const timer = useRef(null);
  const attempt = useRef(0);
  const loadingMore = useRef(false);
  const initialData = useRef(initial);
  const activePath = useRef(path);
  const lastPollPath = useRef(path);
  activePath.current = path;

  // Kept in sync during render (not in an effect) so `loadMore` always paginates
  // from the true current length instead of one render behind.
  const currentDataRef = useRef(state.data);
  currentDataRef.current = state.data;

  useEffect(() => {
    if (!enabled || !path) return undefined;
    let active = true;
    const pathChanged = lastPollPath.current !== path;
    lastPollPath.current = path;
    activePath.current = path;
    attempt.current = 0;
    loadingMore.current = false;
    if (pathChanged) currentDataRef.current = initialData.current;
    setState((prev) => ({
      ...prev,
      path,
      data: prev.path === path ? prev.data : initialData.current,
      error: null,
      lastSuccessAt: null,
      lastAttemptAt: null,
      hasMore: true,
      loadingMore: false,
    }));

    const load = async () => {
      let shouldRetry = true;
      try {
        // Refresh only the newest page instead of re-downloading every row the
        // user has scrolled into view, then merge it over the retained tail.
        // A full refetch grows without bound as `loadMore` accumulates pages.
        const res = await pollGet(buildPagePath(path, pageSize, 0));
        const rows = Array.isArray(res.data) ? res.data : [];
        if (active) {
          attempt.current = 0;
          setState((prev) => {
            const retained = prev.path === path ? prev.data : initialData.current;
            const merged = mergeRefreshedPage(rows, retained, pageSize);
            return {
              ...prev,
              path,
              data: rowsEqual(prev.data, merged) ? prev.data : merged,
              error: null,
              lastSuccessAt: Date.now(),
              hasMore: prev.path === path
                ? prev.hasMore || rows.length >= pageSize
                : rows.length >= pageSize,
              loadingMore: false,
            };
          });
        }
      } catch (error) {
        if (active) {
          attempt.current += 1;
          const is401 = error?.response?.status === 401;
          setState((prev) => ({
            ...prev,
            error: getErrorMessage(error),
            lastAttemptAt: Date.now(),
          }));
          if (is401) shouldRetry = false;
        }
      } finally {
        if (active && shouldRetry) {
          const retryDelay = pollRetryDelay(interval, attempt.current);
          const jitter = Math.round(Math.random() * Math.min(1000, retryDelay * 0.15));
          timer.current = setTimeout(load, (attempt.current ? retryDelay : interval) + jitter);
        }
      }
    };

    load();
    return () => {
      active = false;
      clearTimeout(timer.current);
    };
  }, [path, interval, pageSize, prependOnLoadMore, retryKey, enabled]);

  // Read length/hasMore through refs so this callback keeps a stable identity
  // across data updates; otherwise every poll tick rebuilds the scroll handler.
  const hasMoreRef = useRef(state.hasMore);
  hasMoreRef.current = state.hasMore;

  const loadMore = useCallback(async () => {
    if (!enabled || loadingMore.current || !hasMoreRef.current) return;
    loadingMore.current = true;
    setState((prev) => ({ ...prev, loadingMore: true }));
    try {
      const requestedPath = path;
      const offset = currentDataRef.current?.length || 0;
      const res = await pollGet(buildPagePath(requestedPath, pageSize, offset));
      if (activePath.current !== requestedPath) return;
      const rows = Array.isArray(res.data) ? res.data : [];
      setState((prev) => ({
        ...prev,
        // A background refresh can land while a paged request is in flight.
        // Keep each persisted record exactly once instead of showing duplicates.
        data: dedupeRowsById(
          prependOnLoadMore ? [...rows, ...prev.data] : [...prev.data, ...rows],
        ),
        error: null,
        lastSuccessAt: Date.now(),
        hasMore: rows.length === pageSize,
        loadingMore: false,
      }));
    } catch (error) {
      setState((prev) => ({ ...prev, error: getErrorMessage(error), loadingMore: false }));
    } finally {
      loadingMore.current = false;
    }
  }, [path, pageSize, prependOnLoadMore, enabled]);

  // Stable identity, same reason as usePoll's.
  const retry = useCallback(() => setRetryKey((value) => value + 1), []);

  const scopedState = state.path === path
    ? state
    : {
        path,
        data: initialData.current,
        error: null,
        lastSuccessAt: null,
        lastAttemptAt: null,
        hasMore: true,
        loadingMore: false,
      };
  return {
    ...scopedState,
    isPaused: !enabled,
    isStale: Boolean(scopedState.error && scopedState.lastSuccessAt),
    isUnavailable: Boolean(scopedState.error && !scopedState.lastSuccessAt),
    loadMore,
    retry,
  };
}

export function useInfiniteScroll(loadMore, hasMore = true) {
  return useCallback((event) => {
    const target = event.currentTarget;
    const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (hasMore && remaining <= 96) loadMore();
  }, [hasMore, loadMore]);
}
